import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getClientId } from 'src/module/authentication/selector';
import { getDevicesSelectedFilters } from 'src/module/filter/selector';
import { fetchDeviceNav } from 'src/module/deviceNav/action';
import {
  getDeviceNavError,
  getDeviceNavItems,
  getDeviceNavLoading,
  getDeviceNavTab,
  getDeviceNavTotal
} from 'src/module/deviceNav/selector';
import type { DeviceNavTab } from 'src/service/api/deviceNav';

/* The sidebar's tree for one tab, refetched when the global filters change.
 * The list's filter chips (`attribute`) narrow only the list, never the
 * sidebar's nodes or badges. */
export default function useDeviceNav (tab: DeviceNavTab) {
  const dispatch = useDispatch();

  const clientId = useSelector(getClientId);
  const selectedFilters = useSelector(getDevicesSelectedFilters).delete('attribute');
  const loadedTab = useSelector(getDeviceNavTab);
  const items = useSelector(getDeviceNavItems);
  const total = useSelector(getDeviceNavTotal);
  const loading = useSelector(getDeviceNavLoading);
  const error = useSelector(getDeviceNavError);

  const filtersHash = selectedFilters.hashCode();

  const load = useCallback(() => {
    dispatch(fetchDeviceNav(tab, selectedFilters));
  }, [dispatch, tab, filtersHash]);

  useEffect(load, [load, clientId]);

  // Until the effect runs, the store can still hold the previous tab's tree.
  const current = loadedTab === tab;
  return {
    items: current ? items : [],
    total: current ? total : null,
    loading: loading || !current,
    error,
    retry: load
  };
}
